import {
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    Animated,
    Platform,
    View
} from "react-native";
import type ISwitchProps from "./type";
import type {
    SwitchTrackWebPropsType
} from "./type";
import stylesheet, {
    getSwitchThumbOffset,
    getSwitchStateStyle,
    getSwitchMeasures,
    getSwitchColors,
    getSwitchType,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getMotionDuration,
    useMotionTiming
} from "../../helpers/motion";
import type {
    SelectionControlIndicatorStateType
} from "../selectionControl/type";
import {
    warnOnce
} from "../../utils/development";
import SelectionControl from "../selectionControl";
import StateLayer from "../stateLayer";
import Loading from "../loading";

const Switch: FC<ISwitchProps> = ({
    displayBehaviourWhileLoading,
    spreadBehaviour = "baseline",
    isDefaultChecked = false,
    isDisabled = false,
    animationDuration,
    isLoading = false,
    type = "primary",
    isError = false,
    onCheckedChange,
    size = "medium",
    customTheme,
    isChecked,
    isActive,
    onPress,
    ...props
}) => {
    const {
        shadowAlpha,
        elevations,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        duration: toggleDuration,
        isReduceMotionEnabled,
        easing
    } = useMotionTiming("toggle", customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const [
        innerChecked,
        setInnerChecked
    ] = useState(isDefaultChecked);

    const isControlled = isChecked !== undefined || isActive !== undefined;

    const isOn = isChecked ?? isActive ?? innerChecked;

    const handlePress = () => {
        const nextChecked = !isOn;

        if(!isControlled) {
            setInnerChecked(nextChecked);
        }

        onCheckedChange?.(nextChecked);

        onPress?.();
    };

    const currentOnPress = !isControlled || !!onCheckedChange || !!onPress ? handlePress : undefined;

    const progress = useRef(new Animated.Value(isOn ? 1 : 0)).current;

    const previousIsOnRef = useRef(isOn);

    const currentType = getSwitchType({
        colors,
        type
    });

    const currentMeasures = getSwitchMeasures({
        iconSizes,
        radiuses,
        spaces,
        size
    });

    const currentColors = getSwitchColors({
        isChecked: isOn,
        currentType,
        isDisabled,
        colors
    });

    const thumbOffset = getSwitchThumbOffset({
        currentMeasures,
        direction
    });

    const translateX = useMemo(() => progress.interpolate({
        outputRange: [
            0,
            thumbOffset
        ],
        inputRange: [
            0,
            1
        ]
    }), [
        thumbOffset,
        progress
    ]);

    const {
        trackContainer: trackContainerDynamicStyle,
        track: trackDynamicStyle,
        thumb: thumbDynamicStyle
    } = useStyles({
        currentMeasures,
        currentColors,
        shadowAlpha,
        elevations,
        isDisabled,
        direction,
        colors
    });

    const trackWebProps: SwitchTrackWebPropsType = Platform.OS === "web" ? {
        dir: direction
    } : {};

    useEffect(() => {
        if(previousIsOnRef.current === isOn) {
            return;
        }

        previousIsOnRef.current = isOn;

        const toValue = isOn ? 1 : 0;

        const duration = animationDuration === undefined ? toggleDuration : getMotionDuration({
            duration: animationDuration,
            isReduceMotionEnabled,
            durations
        });

        if(!duration) {
            progress.setValue(toValue);

            return;
        }

        const animation = Animated.timing(progress, {
            useNativeDriver: Platform.OS !== "web",
            duration,
            toValue,
            easing
        });

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        isOn
    ]);

    useEffect(() => {
        if(isActive === undefined) {
            return;
        }

        warnOnce({
            message: "Switch: \"isActive\" is deprecated and will be removed in the next major version. Use \"isChecked\" instead."
        });
    }, [
        isActive
    ]);

    useEffect(() => {
        if(displayBehaviourWhileLoading === undefined) {
            return;
        }

        warnOnce({
            message: "Switch: \"displayBehaviourWhileLoading\" is deprecated and will be removed in the next major version. A loading Switch keeps its type colour, shows the spinner in the thumb and always blocks presses."
        });
    }, [
        displayBehaviourWhileLoading
    ]);

    const renderSpinner = () => {
        if(!isLoading) {
            return null;
        }

        return <Loading
            customColor={currentColors.spinnerColor}
            size={currentMeasures.thumbSize}
            customTheme={customTheme}
        />;
    };

    const renderIndicator = ({
        isFocusVisible,
        isHovered,
        isPressed
    }: SelectionControlIndicatorStateType) => {
        const {
            track: trackStateStyle
        } = getSwitchStateStyle({
            isReduceMotionEnabled,
            isFocusVisible,
            durations,
            borders,
            colors,
            type
        });

        return <View
            style={[
                stylesheet.trackContainer,
                trackContainerDynamicStyle
            ]}
        >
            <View
                {...trackWebProps}
                style={[
                    stylesheet.track,
                    trackDynamicStyle,
                    trackStateStyle
                ]}
            >
                <StateLayer
                    borderRadius={currentMeasures.borderRadius}
                    overlayColor={currentColors.overlayColor}
                    customTheme={customTheme}
                    isHovered={isHovered}
                    isPressed={isPressed}
                />
                <Animated.View
                    style={[
                        stylesheet.thumb,
                        thumbDynamicStyle,
                        {
                            transform: [
                                {
                                    translateX
                                }
                            ]
                        }
                    ]}
                >
                    {renderSpinner()}
                </Animated.View>
            </View>
        </View>;
    };

    return <SelectionControl
        {...props}
        renderIndicator={renderIndicator}
        spreadBehaviour={spreadBehaviour}
        customTheme={customTheme}
        onPress={currentOnPress}
        isDisabled={isDisabled}
        isLoading={isLoading}
        checkedState={isOn}
        isError={isError}
        role="switch"
    />;
};
export default Switch;
